/**
 * InversePay logo, exported from the Figma B2B V2.0 file (node 7:3526).
 *
 * Inlined rather than served as a file so it costs no extra request and stays
 * crisp at any size. The three fills are the brand tokens — ink `#17112A`,
 * royal purple `#5B2D90` and violet `#9B51E0` — kept as literals here because
 * this is the brand asset itself, not a themed surface.
 */

/** The chevron mark on its own — used when the sidebar is collapsed. */
export function LogoMark({ className, title = 'InversePay' }) {
  return (
    <svg
      viewBox="0 0 21.4353 36"
      className={className}
      role="img"
      aria-label={title}
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M0 31.188C4.20425 22.3267 4.18423 13.3985 0.0133395 4.66416C3.12983 9.70964 7.2273 14.1737 12.159 17.9161C7.254 21.625 3.18322 26.069 0 31.1813V31.188Z"
        fill="#5B2D90"
      />
      <path
        opacity="0.5"
        d="M8.47539 36C11.2982 30.5068 12.7664 24.6727 12.8665 18.4511C15.5626 20.3958 18.3921 22.0397 21.4352 23.423C16.1231 26.6374 11.8188 30.8209 8.47539 36Z"
        fill="#9B51E0"
      />
      <path
        opacity="0.5"
        d="M21.4352 12.4299C18.3921 13.8066 15.5692 15.4438 12.8665 17.3952C12.7664 11.2404 11.3316 5.45313 8.56882 0C11.9055 5.10562 16.1698 9.24225 21.4352 12.4299Z"
        fill="#9B51E0"
      />
    </svg>
  );
}

/** Full lockup — wordmark plus mark, as it appears in the Figma sidebar. */
export default function Logo({ className, title = 'InversePay' }) {
  return (
    <svg
      viewBox="0 0 112 36"
      className={className}
      role="img"
      aria-label={title}
      xmlns="http://www.w3.org/2000/svg"
    >
      {/* Wordmark — vertically centred in the 36px lockup. */}
      <g transform="translate(0, 10.6089)">
        <path
          d="M3.55029 12.8975C3.55029 13.8532 2.78952 14.615 1.77516 14.615C0.7608 14.615 3.02307e-05 13.8799 3.02307e-05 12.951C3.02307e-05 11.9686 0.734106 11.2001 1.77516 11.2001C2.81621 11.2001 3.55029 11.9352 3.55029 12.8909V12.8975Z"
          fill="#9B51E0"
        />
        <path d="M3.08998 0.00626079H0.474V9.23515H3.08998V0.00626079Z" fill="#17112A" />
        <path
          d="M20.4337 6.28805V14.6214H17.8177V6.58877C17.8177 3.7486 16.3963 2.35191 13.9138 2.35191C11.131 2.35191 9.32916 4.01591 9.32916 7.15681V14.6148H6.71318V0.139916H9.22239V2.32518C10.2835 0.848288 12.1387 0.00626079 14.4343 0.00626079C17.9512 0.00626079 20.4337 2.02445 20.4337 6.28805Z"
          fill="#17112A"
        />
        <path
          d="M36.4502 0.140081L30.1238 14.6149H27.4478L21.1214 0.140081H23.8508L28.8158 11.748L33.8876 0.140081H36.4502Z"
          fill="#17112A"
        />
        <path
          d="M50.1436 8.25323H38.4117C38.7387 10.7927 40.7875 12.4901 43.6504 12.4901C45.3387 12.4901 46.7602 11.9154 47.8213 10.7392L49.2694 12.4299C47.9614 13.9603 45.966 14.7823 43.5703 14.7823C38.9056 14.7823 35.7958 11.6948 35.7958 7.37778C35.7958 3.06073 38.8789 2.33881e-05 43.0498 2.33881e-05C47.2207 2.33881e-05 50.197 3.00726 50.197 7.45798C50.197 7.67851 50.1703 8.00596 50.1436 8.25323ZM38.4117 6.36869H47.6878C47.4142 3.93617 45.5857 2.2187 43.0498 2.2187C40.5139 2.2187 38.6853 3.90943 38.4117 6.36869Z"
          fill="#17112A"
        />
        <path
          d="M60.2345 0.00626079V2.54571C60.0143 2.51898 59.8275 2.51898 59.6339 2.51898C56.8244 2.51898 55.076 4.24313 55.076 7.41075V14.6214H52.46V0.139916H54.9692V2.57244C55.8968 0.881701 57.6986 0.00626079 60.2345 0.00626079Z"
          fill="#17112A"
        />
        <path
          d="M60.3674 13.0911L61.4619 11.0128C62.6898 11.8882 64.6518 12.5164 66.5337 12.5164C68.9628 12.5164 69.9705 11.7813 69.9705 10.5516C69.9705 7.30383 60.8346 10.1173 60.8346 4.35005C60.8346 1.75714 63.1502 0.00626079 66.8607 0.00626079C68.7426 0.00626079 70.8714 0.500785 72.126 1.31608L71.0048 3.39442C69.6969 2.54571 68.2487 2.24498 66.834 2.24498C64.545 2.24498 63.4238 3.09369 63.4238 4.23644C63.4238 7.65133 72.5864 4.86462 72.5864 10.4915C72.5864 13.1111 70.184 14.7818 66.3401 14.7818C63.9377 14.7818 61.5686 14.0467 60.3674 13.0911Z"
          fill="#17112A"
        />
        <path
          d="M87.5085 8.25323H75.7766C76.1036 10.7927 78.1523 12.4901 81.0152 12.4901C82.7036 12.4901 84.125 11.9154 85.1861 10.7392L86.6343 12.4299C85.3263 13.9603 83.3309 14.7823 80.9352 14.7823C76.2704 14.7823 73.1606 11.6948 73.1606 7.37778C73.1606 3.06073 76.2437 2.33881e-05 80.4146 2.33881e-05C84.5855 2.33881e-05 87.5619 3.00726 87.5619 7.45798C87.5619 7.67851 87.5352 8.00596 87.5085 8.25323ZM75.7833 6.36869H85.0593C84.7857 3.93617 82.9572 2.2187 80.4213 2.2187C77.8854 2.2187 76.0569 3.90943 75.7833 6.36869Z"
          fill="#17112A"
        />
      </g>

      {/* Chevron mark — right-aligned in the lockup. */}
      <g transform="translate(90.5647, 0)">
        <path
          d="M0 31.188C4.20425 22.3267 4.18423 13.3985 0.0133395 4.66416C3.12983 9.70964 7.2273 14.1737 12.159 17.9161C7.254 21.625 3.18322 26.069 0 31.1813V31.188Z"
          fill="#5B2D90"
        />
        <path
          opacity="0.5"
          d="M8.47539 36C11.2982 30.5068 12.7664 24.6727 12.8665 18.4511C15.5626 20.3958 18.3921 22.0397 21.4352 23.423C16.1231 26.6374 11.8188 30.8209 8.47539 36Z"
          fill="#9B51E0"
        />
        <path
          opacity="0.5"
          d="M21.4352 12.4299C18.3921 13.8066 15.5692 15.4438 12.8665 17.3952C12.7664 11.2404 11.3316 5.45313 8.56882 0C11.9055 5.10562 16.1698 9.24225 21.4352 12.4299Z"
          fill="#9B51E0"
        />
      </g>
    </svg>
  );
}
